// Match Type Distribution — Keywords section, LLM judgment row.
// Renders Claude's verdict on whether the EXACT/PHRASE/BROAD mix is coherent with the
// account's bidding strategy, plus account-level + per-campaign distribution detail.

import type { EvidenceProps } from './registry';
import { pct } from '../../utils/format';

interface CampaignMatchTypeRow {
  campaignId?: string;
  campaignName?: string;
  biddingStrategy?: string;
  totalKeywords: number;
  exact: number;
  phrase: number;
  broad: number;
  other: number;
}

interface AccountDistribution {
  totalKeywords: number;
  exactCount: number;
  phraseCount: number;
  broadCount: number;
  otherCount: number;
  exactPct: number;
  phrasePct: number;
  broadPct: number;
}

interface MatchTypeShape {
  distribution: AccountDistribution;
  campaigns: CampaignMatchTypeRow[];
}

const STRATEGY_LABEL: Record<string, string> = {
  MANUAL_CPC: 'Manual CPC',
  ENHANCED_CPC: 'Enhanced CPC',
  MAXIMIZE_CONVERSIONS: 'Max Conversions',
  MAXIMIZE_CONVERSION_VALUE: 'Max Conv. Value',
  TARGET_CPA: 'Target CPA',
  TARGET_ROAS: 'Target ROAS',
  TARGET_IMPRESSION_SHARE: 'Target Imp. Share',
  TARGET_SPEND: 'Target Spend',
  PERCENT_CPC: 'Percent CPC'
};

function strategyLabel(s: string | undefined): string {
  if (!s) return '—';
  return STRATEGY_LABEL[s] ?? s;
}

export const MatchTypeDistributionEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as MatchTypeShape;
  const d = e.distribution;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Account-level mix
          <span className='evidence__section-tag evidence__section-tag--context'>
            {d.totalKeywords} keywords
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{pct(d.exactPct)}</div>
            <div className='audience-coverage__lbl'>Exact ({d.exactCount})</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{pct(d.phrasePct)}</div>
            <div className='audience-coverage__lbl'>Phrase ({d.phraseCount})</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{pct(d.broadPct)}</div>
            <div className='audience-coverage__lbl'>Broad ({d.broadCount})</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Per-campaign breakdown
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.campaigns.length} campaigns
          </span>
        </h4>
        <table className='evidence__table evidence__table--match-type'>
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Bidding strategy</th>
              <th className='evidence__th--right'>Keywords</th>
              <th className='evidence__th--right'>Exact</th>
              <th className='evidence__th--right'>Phrase</th>
              <th className='evidence__th--right'>Broad</th>
            </tr>
          </thead>
          <tbody>
            {e.campaigns.map(c => {
              const e_ = c.totalKeywords > 0 ? c.exact / c.totalKeywords : 0;
              const p_ = c.totalKeywords > 0 ? c.phrase / c.totalKeywords : 0;
              const b_ = c.totalKeywords > 0 ? c.broad / c.totalKeywords : 0;
              return (
                <tr key={c.campaignId ?? c.campaignName}>
                  <td>{c.campaignName ?? '(unnamed)'}</td>
                  <td>{strategyLabel(c.biddingStrategy)}</td>
                  <td className='evidence__td--right'>{c.totalKeywords.toLocaleString()}</td>
                  <td className='evidence__td--right'>{pct(e_)}</td>
                  <td className='evidence__td--right'>{pct(p_)}</td>
                  <td className='evidence__td--right'>{pct(b_)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
};
